import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './ProcessingOrchestrationMonitor.css';
import './ProcessingOrchestrationTableControls.css';
import { buildApiUrl, getApiHeaders } from './config';
import { getAuthToken } from './auth/authUtils';
import TradingViewChartIcon from './TradingViewChartIcon';

const TABS = ['Overview', 'Processing Cycles', 'Stage Timings', 'Boundary Readiness', 'Batches',
  'Trade Lifecycle', 'MTF Confluence', 'Issues', 'Scheduler Health', 'Symbol Universe'];
const STATUSES = ['PENDING', 'READY', 'IN_PROGRESS', 'COMPLETED', 'BLOCKED', 'SKIPPED', 'FAILED', 'DEAD'];
const TIMED_STAGES = ['INCOMING_QUEUE_WAIT', 'MARKET_DATA_INGESTION', 'SIGNAL_GENERATION', 'MARKET_PULSE',
  'TRADE_ENTRY_TRACKER', 'MTF_CONFLUENCE_ENTRY', 'TAKE_PROFIT_EVALUATION', 'VOLUME_RANK'];

const headers = () => getApiHeaders({ Authorization: `Bearer ${getAuthToken()}` });
const formatDate = value => value ? new Date(value).toLocaleString() : '-';
const badge = value => <span className={`orchestration-badge status-${String(value || '').toLowerCase()}`}>{value || '-'}</span>;
const symbolsFrom = rows => [...new Set(rows.map(row => row.symbol).filter(Boolean))].sort();

async function api(path, params, options = {}) {
  const response = await fetch(buildApiUrl(path, params), {
    ...options,
    headers: { ...headers(), 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  if (!response.ok) {
    const error = new Error(response.status === 404 && path.endsWith('/stage-timings')
      ? 'Stage timings API is not available in the deployed bot backend. Deploy the matching bot-app version.'
      : `Request failed (${response.status})`);
    error.status = response.status;
    error.path = path;
    throw error;
  }
  return response.json();
}

export default function ProcessingOrchestrationMonitor({ workspace, refreshScopes = () => {}, universeOnly = false }) {
  const [tab, setTab] = useState(universeOnly ? 'Symbol Universe' : TABS[0]);
  const [summary, setSummary] = useState({});
  const [configuration, setConfiguration] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ timeFrame: '', stage: '', status: '', symbol: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [details, setDetails] = useState(null);
  const [missingSymbols, setMissingSymbols] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const [tableResetKey, setTableResetKey] = useState(0);
  const [stagePerformance, setStagePerformance] = useState([]);
  const loadSequence = useRef(0);

  const scope = useMemo(() => ({ exchange: workspace.exchange, marketType: workspace.marketType }), [workspace]);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    const applyRows = (data, totalPages = 1) => {
      if (sequence !== loadSequence.current) return;
      setRows(data);
      setPages(totalPages);
    };
    setLoading(true);
    setError('');
    try {
      // Universe administration must not depend on expensive monitoring queries.
      if (tab === 'Symbol Universe') {
        const universe = await api('/api/bot/ui/orchestration/symbol-universe/all');
        if (sequence !== loadSequence.current) return;
        applyRows(universe);
        return;
      }
      const [summaryData, configData] = await Promise.all([
        api('/api/bot/ui/orchestration/summary', scope),
        api('/api/bot/ui/orchestration/configuration'),
      ]);
      if (sequence !== loadSequence.current) return;
      setSummary(summaryData);
      setConfiguration(configData);
      if (tab === 'Overview') {
        const performance = await api('/api/bot/ui/orchestration/stage-performance', { ...scope, hours: 24 });
        if (sequence !== loadSequence.current) return;
        setStagePerformance(performance);
        applyRows([]);
      }
      if (tab === 'Processing Cycles') {
        const cycleFilters = { timeFrame: filters.timeFrame, status: filters.status, symbol: filters.symbol };
        const data = await api('/api/bot/ui/orchestration/cycles', { ...scope, ...cycleFilters, page, size: 50 });
        applyRows(data.content || [], data.totalPages || 1);
      } else if (tab === 'Stage Timings') {
        const data = await api('/api/bot/ui/orchestration/stage-timings', {
          ...scope, ...filters, hours: 24, page, size: 50,
        });
        applyRows(data.content || [], data.totalPages || 1);
      } else if (tab === 'Boundary Readiness') {
        const data = await api('/api/bot/ui/orchestration/boundary-readiness', { ...scope, hours: 24, page, size: 50 });
        applyRows(data.content || [], data.totalPages || 1);
      } else if (tab === 'Batches') {
        const data = await api('/api/bot/ui/orchestration/batches', { ...scope, page, size: 50 });
        applyRows(data.content || [], data.totalPages || 1);
      } else if (tab === 'Trade Lifecycle') {
        const data = await api('/api/bot/ui/orchestration/trade-lifecycle', { ...scope, hours: 24, page, size: 50 });
        applyRows(data.content || [], data.totalPages || 1);
      } else if (tab === 'MTF Confluence') {
        const data = await api('/api/bot/ui/orchestration/mtf-confluence',
          { ...scope, status: filters.status, symbol: filters.symbol, page, size: 50 });
        applyRows(data.content || [], data.totalPages || 1);
      } else if (tab === 'Issues') {
        const data = await api('/api/bot/ui/orchestration/issues', { ...scope, page, size: 50 });
        applyRows(data.content || [], data.totalPages || 1);
      } else if (tab === 'Scheduler Health') {
        applyRows(await api('/api/bot/ui/orchestration/schedulers'));

      }
    } catch (requestError) {
      if (sequence !== loadSequence.current) return;
      setRows([]);
      setPages(1);
      setError(requestError.message);
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [filters, page, scope, tab]);

  useEffect(() => {
    load();
    return () => { loadSequence.current += 1; };
  }, [load]);
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [autoRefresh, load]);

  const openDetails = async cycleId => {
    try { setDetails(await api(`/api/bot/ui/orchestration/cycles/${cycleId}`)); }
    catch (requestError) { setError(requestError.message); }
  };

  const openMissingSymbols = async batch => {
    try {
      const data = await api(`/api/bot/ui/orchestration/batches/${batch.batchCycleId}/missing-symbols`, { page: 0, size: 200 });
      setMissingSymbols({ batch, symbols: data.content || [], total: data.totalElements || 0 });
    } catch (requestError) { setError(requestError.message); }
  };

  const addSymbol = async () => {
    const symbol = newSymbol.trim();
    if (!symbol) return;
    try {
      await api('/api/bot/ui/orchestration/symbol-universe', undefined, {
        method: 'POST', body: JSON.stringify({ ...scope, symbol, active: true }),
      });
      setNewSymbol(''); refreshScopes(); await load();
    } catch (requestError) { setError(requestError.message); }
  };

  const setSymbolActive = async (exchange, marketType, symbol, active) => {
    try {
      await api(`/api/bot/ui/orchestration/symbol-universe/symbol/${encodeURIComponent(symbol)}/active`,
        { exchange, marketType }, {
        method: 'PATCH', body: JSON.stringify({ active }),
      });
      refreshScopes();
      await load();
    } catch (requestError) { setError(requestError.message); }
  };

  const setTradingAllowed = async (ids, tradingAllowed) => {
    try {
      await Promise.all(ids.map(id => api(
        `/api/bot/ui/orchestration/symbol-universe/${id}/trading-allowed`,
        undefined,
        { method: 'PATCH', body: JSON.stringify({ tradingAllowed }) }
      )));
      await load();
    } catch (requestError) { setError(requestError.message); }
  };

  const clearTableFilters = () => {
    setFilters({ timeFrame: '', stage: '', status: '', symbol: '' });
    setTableResetKey(value => value + 1);
    setPage(0);
  };

  return <section className="orchestration-monitor">
    <div className="orchestration-heading">
      <div><h2>Scheduler & Orchestration Monitor</h2><p>{universeOnly ? 'All data sources' : `${workspace.exchange} / ${workspace.marketType}`}</p>
      </div>
      <div className="orchestration-actions">
        <label><input type="checkbox" checked={autoRefresh} onChange={event => setAutoRefresh(event.target.checked)} /> Auto-refresh</label>
        <button onClick={load} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
      </div>
    </div>
    {error && <div className="orchestration-error" role="alert">{error}. Data could not be loaded; refresh to retry.</div>}
    <nav className="orchestration-tabs">
      {(universeOnly ? ['Symbol Universe'] : TABS).map(name => <button key={name} className={tab === name ? 'active' : ''}
        onClick={() => { setTab(name); setPage(0); setRows([]); setPages(1); setError(''); setDetails(null); }}>{name}</button>)}
    </nav>

    {tab === 'Overview' && <><div className="orchestration-summary">
      {STATUSES.map(status => <article key={status}><span>{status.replace('_', ' ')}</span><strong>{summary[status] || 0}</strong></article>)}
      <article><span>Configuration version</span><strong>{configuration?.configurationVersion || '-'}</strong></article>
    </div><StagePerformanceTable rows={stagePerformance} resetKey={tableResetKey} /></>}

    {tab === 'Processing Cycles' && <>
      <div className="orchestration-filters">
        <SymbolFilter value={filters.symbol} rows={rows} onChange={symbol => setFilters({...filters, symbol})} />
        <select value={filters.timeFrame} onChange={e => setFilters({...filters, timeFrame: e.target.value})}>
          <option value="">All timeframes</option>{configuration?.allowedTimeFrames?.map(tf => <option key={tf} value={tf}>{tf} minutes</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}>
          <option value="">All statuses</option>{configuration?.statuses?.map(value => <option key={value}>{value}</option>)}
        </select>
        <ClearFiltersButton onClick={clearTableFilters} />
      </div>
      <p className="orchestration-help"><strong>Cycle checkpoint</strong> is the durable signal-cycle state. Use Stage Timings or View for actual stage executions and durations.</p>
      <CycleTable rows={rows} onDetails={openDetails} resetKey={tableResetKey} page={page} />
    </>}
    {tab === 'Stage Timings' && <>
      <div className="orchestration-filters">
        <SymbolFilter label="Timing symbol" value={filters.symbol} rows={rows} onChange={symbol => setFilters({...filters, symbol})} />
        <select value={filters.timeFrame} onChange={e => setFilters({...filters, timeFrame: e.target.value})}>
          <option value="">All timeframes</option>{configuration?.allowedTimeFrames?.map(tf => <option key={tf} value={tf}>{tf} minutes</option>)}
        </select>
        <select aria-label="Timing stage" value={filters.stage} onChange={e => setFilters({...filters, stage: e.target.value})}>
          <option value="">All executed stages</option>{TIMED_STAGES.map(value => <option key={value}>{value}</option>)}
        </select>
        <select aria-label="Timing status" value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}>
          <option value="">All statuses</option>{configuration?.statuses?.map(value => <option key={value}>{value}</option>)}
        </select>
        <ClearFiltersButton onClick={clearTableFilters} />
      </div>
      <p className="orchestration-help">Showing stage executions from the latest 24 hours.</p>
      <StageTimingTable rows={rows} resetKey={tableResetKey} page={page} />
    </>}
    {tab === 'Boundary Readiness' && <TableTab onClear={clearTableFilters}>
      <BoundaryReadinessTable rows={rows} resetKey={tableResetKey} page={page} />
    </TableTab>}
    {tab === 'Batches' && <TableTab onClear={clearTableFilters}><BatchTable rows={rows} onMissing={openMissingSymbols} resetKey={tableResetKey} page={page} /></TableTab>}
    {tab === 'Trade Lifecycle' && <TableTab onClear={clearTableFilters}>
      <TradeLifecycleTable rows={rows} resetKey={tableResetKey} page={page} />
    </TableTab>}
    {tab === 'MTF Confluence' && <>
      <div className="orchestration-filters">
        <SymbolFilter label="MTF symbol" value={filters.symbol} rows={rows}
          onChange={symbol => setFilters({...filters, symbol})} />
        <select value={filters.status} onChange={e => setFilters({...filters, status: e.target.value})}>
          <option value="">All statuses</option>{configuration?.statuses?.map(value => <option key={value}>{value}</option>)}
        </select>
        <ClearFiltersButton onClick={clearTableFilters} />
      </div>
      <StageTimingTable rows={rows} resetKey={tableResetKey} page={page} />
    </>}
    {tab === 'Issues' && <TableTab onClear={clearTableFilters}><CycleTable rows={rows} onDetails={openDetails} issues resetKey={tableResetKey} page={page} /></TableTab>}
    {tab === 'Scheduler Health' && <TableTab onClear={clearTableFilters}><SchedulerTable rows={rows} resetKey={tableResetKey} /></TableTab>}
    {tab === 'Symbol Universe' && <>
      {!universeOnly && <div className="orchestration-filters">
        <input aria-label="New symbol" placeholder="Add symbol (for example BTCUSDT.P)" value={newSymbol}
          onChange={event => setNewSymbol(event.target.value)} />
        <button onClick={addSymbol} disabled={!newSymbol.trim()}>Add across allowed timeframes</button>
      </div>}
      <ClearFiltersButton onClick={clearTableFilters} />
      {!error && !loading && <SymbolUniverseTable rows={rows} onSetActive={setSymbolActive}
        onSetTradingAllowed={setTradingAllowed} resetKey={tableResetKey} />
      }
    </>}
    {tab !== 'Overview' && tab !== 'Scheduler Health' && tab !== 'Symbol Universe' && <Pagination page={page} pages={pages} setPage={setPage} />}
    {details && <CycleDetails data={details} onClose={() => setDetails(null)} />}
    {missingSymbols && <MissingSymbolsDialog data={missingSymbols} onClose={() => setMissingSymbols(null)} />}
  </section>;
}

function ClearFiltersButton({ onClick }) {
  return <button className="orchestration-clear-filters" onClick={onClick}>
    <span className="orchestration-clear-filters__icon" aria-hidden="true" /><span>Clear Filters</span>
  </button>;
}

function SymbolFilter({ value, rows, onChange, label = 'Symbol' }) {
  const listId = `orchestration-${label.toLowerCase().replace(/\s+/g, '-')}-options`;
  return <><input aria-label={label} list={listId} placeholder="Symbol" value={value}
    onChange={event => onChange(event.target.value)} />
  <datalist id={listId}>{symbolsFrom(rows).map(symbol => <option key={symbol} value={symbol} />)}</datalist></>;
}

function TableTab({ onClear, children }) {
  return <><div className="orchestration-table-actions"><ClearFiltersButton onClick={onClear} /></div>{children}</>;
}

function CycleTable({ rows, onDetails, issues, resetKey, page = 0 }) {
  const columns = [
    ['serialNumber', 'S.No', (_, index) => page * 50 + index + 1, 'numeric'],
    ['cycleId', 'Cycle ID', row => row.cycleId], ['symbol', 'Symbol', row => row.symbol, 'select'],
    ['chart', 'Chart', () => '', 'none'],
    ['timeFrame', 'Timeframe', row => `${row.timeFrame ?? ''}m`, 'select'], ['candleCloseTime', 'Candle Close', row => row.candleCloseTime],
    ['stage', 'Cycle checkpoint', row => row.stage, 'select'], ['status', 'Status', row => row.status, 'select'],
    ['triggerSource', 'Trigger', row => row.triggerSource, 'select'],
    ['processingDelayMs', 'Signal delay', row => row.processingDelayMs == null ? '-' : `${row.processingDelayMs} ms`, 'numeric'],
    ['result', issues ? 'Issue' : 'Retries', row => issues ? (row.reason || '-') : row.retryCount, issues ? 'text' : 'numeric'],
    ['details', 'Stage details', () => 'View', 'none'],
  ];
  const { visibleRows, filterHeader, sortableHeader } = useTableControls(rows, columns, resetKey);
  return <div className="orchestration-table"><table><thead><tr>
    {sortableHeader}
  </tr>{filterHeader}</thead><tbody>{visibleRows.map(row => <tr key={row.cycleId}>
    <td>{row.__rowNumber}</td><td title={row.cycleId}>{row.cycleId?.slice(0, 8)}</td><td>{row.symbol}</td>
    <td><TradingViewChartIcon symbol={row.symbol} timeFrame={row.timeFrame}
      exchange={row.exchange} marketType={row.marketType} /></td><td>{row.timeFrame}m</td>
    <td>{row.candleCloseTime}</td><td>{row.stage}</td><td>{badge(row.status)}</td><td>{row.triggerSource}</td>
    <td>{row.processingDelayMs == null ? '-' : `${row.processingDelayMs} ms`}</td>
    <td>{issues ? (row.reason || '-') : row.retryCount}</td><td><button onClick={() => onDetails(row.cycleId)}>View</button></td>
  </tr>)}</tbody></table><FilteredEmpty rows={rows} visibleRows={visibleRows} empty="No records found." /></div>;
}

function StageTimingTable({ rows, resetKey, page = 0 }) {
  const columns = [
    ['serialNumber', 'S.No', (_, index) => page * 50 + index + 1, 'numeric'],
    ['symbol', 'Symbol', row => row.symbol, 'select'],
    ['chart', 'Chart', () => '', 'none'],
    ['timeFrame', 'TF', row => `${row.timeFrame ?? ''}m`, 'select'],
    ['candleCloseTime', 'Candle Close', row => row.candleCloseTime],
    ['stage', 'Stage', row => row.stage, 'select'],
    ['status', 'Status', row => row.status, 'select'],
    ['startedAt', 'Started', row => formatDate(row.startedAt)],
    ['completedAt', 'Completed', row => formatDate(row.completedAt)],
    ['durationMs', 'Duration', row => row.durationMs == null ? (row.status === 'IN_PROGRESS' ? 'Running' : '-') : `${row.durationMs} ms`, 'numeric'],
    ['triggerSource', 'Trigger', row => row.triggerSource, 'select'],
    ['attemptNumber', 'Attempt', row => row.attemptNumber || 0, 'numeric'],
    ['error', 'Error', row => [row.errorCode, row.errorMessage].filter(Boolean).join(' ') || '-'],
  ];
  const { visibleRows, filterHeader, sortableHeader } = useTableControls(rows, columns, resetKey);
  return <div className="orchestration-table"><table><thead><tr>
    {sortableHeader}
  </tr>{filterHeader}</thead><tbody>{visibleRows.map(row => <tr key={row.id}>
    <td>{row.__rowNumber}</td><td>{row.symbol}</td><td><TradingViewChartIcon symbol={row.symbol}
      timeFrame={row.timeFrame} exchange={row.exchange} marketType={row.marketType} /></td>
    <td>{row.timeFrame}m</td><td>{row.candleCloseTime}</td><td>{row.stage}</td><td>{badge(row.status)}</td>
    <td>{formatDate(row.startedAt)}</td><td>{formatDate(row.completedAt)}</td>
    <td>{row.durationMs == null ? (row.status === 'IN_PROGRESS' ? 'Running' : '-') : `${row.durationMs} ms`}</td>
    <td>{row.triggerSource}</td><td>{row.attemptNumber || 0}</td><td title={row.errorMessage || ''}>{row.errorCode || '-'}</td>
  </tr>)}</tbody></table>{!visibleRows.length && <p className="orchestration-empty">
    {rows.length ? 'No stage attempts match the column filters.' : 'No stage attempts found.'}</p>}</div>;
}

function StagePerformanceTable({ rows, resetKey }) {
  const columns = [
    ['serialNumber', 'S.No', (_, index) => index + 1, 'numeric'],
    ['stage', 'Stage', row => row.stage, 'select'], ['status', 'Status', row => row.status, 'select'],
    ['executions', 'Executions (24h)', row => row.executions, 'numeric'],
    ['averageDurationMs', 'Average ms', row => row.averageDurationMs, 'numeric'],
    ['maximumDurationMs', 'Maximum ms', row => row.maximumDurationMs, 'numeric'],
    ['fallbackExecutions', 'Fallback', row => row.fallbackExecutions, 'numeric'],
    ['failedExecutions', 'Failures', row => row.failedExecutions, 'numeric'],
    ['latestCompletion', 'Latest completion', row => formatDate(row.latestCompletion)],
  ];
  const { visibleRows, filterHeader, sortableHeader } = useTableControls(rows, columns, resetKey);
  return <div className="orchestration-table orchestration-analytics-table"><h3>Stage performance — last 24 hours</h3>
    <table><thead><tr>{sortableHeader}</tr>{filterHeader}</thead><tbody>{visibleRows.map(row =>
      <tr key={`${row.stage}-${row.status}`}><td>{row.__rowNumber}</td><td>{row.stage}</td><td>{badge(row.status)}</td>
        <td>{row.executions}</td><td>{Number(row.averageDurationMs || 0).toFixed(1)}</td>
        <td>{row.maximumDurationMs}</td><td>{row.fallbackExecutions}</td><td>{row.failedExecutions}</td>
        <td>{formatDate(row.latestCompletion)}</td></tr>)}</tbody></table>
    <FilteredEmpty rows={rows} visibleRows={visibleRows} empty="No stage performance recorded in the last 24 hours." />
  </div>;
}

function BoundaryReadinessTable({ rows, resetKey, page = 0 }) {
  const requiredCount = row => Number(row.requiredTimeFrameCount || 0);
  const columns = [
    ['serialNumber', 'S.No', (_, index) => page * 50 + index + 1, 'numeric'],
    ['symbol', 'Symbol', row => row.symbol, 'select'], ['candleCloseTime', 'Boundary', row => row.candleCloseTime],
    ['requiredTimeFrames', 'Required TFs', row => row.requiredTimeFrames || '-'],
    ['completedSignalTimeFrames', 'Signals ready', row => row.completedSignalTimeFrames, 'numeric'],
    ['readiness', 'Gate', row => Number(row.completedSignalTimeFrames) >= requiredCount(row) ? 'READY' : 'WAITING', 'select'],
    ['tradeEntryStatus', 'Trade Entry', row => row.tradeEntryStatus || 'NOT_STARTED', 'select'],
    ['mtfConfluenceStatus', 'MTF', row => row.mtfConfluenceStatus || 'NOT_STARTED', 'select'],
    ['takeProfitStatus', 'Take Profit', row => row.takeProfitStatus || 'NOT_STARTED', 'select'],
    ['fallbackUsed', 'Fallback', row => row.fallbackUsed ? 'YES' : 'NO', 'select'],
    ['latestActivity', 'Latest activity', row => formatDate(row.latestActivity)],
  ];
  const { visibleRows, filterHeader, sortableHeader } = useTableControls(rows, columns, resetKey);
  return <div className="orchestration-table"><table><thead><tr>{sortableHeader}</tr>{filterHeader}</thead><tbody>
    {visibleRows.map(row => <tr key={`${row.symbol}-${row.candleCloseTime}`}><td>{row.__rowNumber}</td><td>{row.symbol}</td>
      <td>{row.candleCloseTime}</td><td>{row.requiredTimeFrames || '-'}</td><td>{row.completedSignalTimeFrames}</td>
      <td>{badge(Number(row.completedSignalTimeFrames) >= requiredCount(row) ? 'READY' : 'WAITING')}</td>
      <td>{badge(row.tradeEntryStatus || 'NOT_STARTED')}</td><td>{badge(row.mtfConfluenceStatus || 'NOT_STARTED')}</td>
      <td>{badge(row.takeProfitStatus || 'NOT_STARTED')}</td><td>{row.fallbackUsed ? 'YES' : 'NO'}</td>
      <td>{formatDate(row.latestActivity)}</td></tr>)}</tbody></table>
    <FilteredEmpty rows={rows} visibleRows={visibleRows} empty="No recent boundaries found." /></div>;
}

function TradeLifecycleTable({ rows, resetKey, page = 0 }) {
  const columns = [
    ['serialNumber', 'S.No', (_, index) => page * 50 + index + 1, 'numeric'],
    ['symbol', 'Symbol', row => row.symbol, 'select'], ['timeFrame', 'TF', row => `${row.timeFrame}m`, 'select'],
    ['candleCloseTime', 'Candle Close', row => row.candleCloseTime],
    ['tradeEntryStatus', 'Trade Entry', row => row.tradeEntryStatus || 'NOT_STARTED', 'select'],
    ['tradeEntryDurationMs', 'Entry ms', row => row.tradeEntryDurationMs ?? '-', 'numeric'],
    ['mtfConfluenceStatus', 'MTF Confluence', row => row.mtfConfluenceStatus || 'NOT_STARTED', 'select'],
    ['mtfConfluenceDurationMs', 'MTF ms', row => row.mtfConfluenceDurationMs ?? '-', 'numeric'],
    ['takeProfitStatus', 'Take Profit', row => row.takeProfitStatus || 'NOT_STARTED', 'select'],
    ['takeProfitDurationMs', 'TP ms', row => row.takeProfitDurationMs ?? '-', 'numeric'],
    ['dcaStatus', 'DCA', row => row.dcaStatus || 'NOT_REQUIRED', 'select'],
    ['breakEvenStatus', 'Break Even', row => row.breakEvenStatus || 'NOT_REQUIRED', 'select'],
    ['partialTakeProfitStatus', 'Partial TP', row => row.partialTakeProfitStatus || 'NOT_REQUIRED', 'select'],
    ['trailingStopLossStatus', 'Trailing SL', row => row.trailingStopLossStatus || 'NOT_REQUIRED', 'select'],
    ['triggerSource', 'Trigger', row => row.triggerSource || '-', 'select'],
    ['lifecycleComment', 'Lifecycle detail', row => row.lifecycleComment || '-'],
    ['errorMessage', 'Error', row => row.errorMessage || '-'],
  ];
  const { visibleRows, filterHeader, sortableHeader } = useTableControls(rows, columns, resetKey);
  return <div className="orchestration-table"><table><thead><tr>{sortableHeader}</tr>{filterHeader}</thead><tbody>
    {visibleRows.map(row => <tr key={row.cycleId}><td>{row.__rowNumber}</td><td>{row.symbol}</td><td>{row.timeFrame}m</td>
      <td>{row.candleCloseTime}</td><td>{badge(row.tradeEntryStatus || 'NOT_STARTED')}</td><td>{row.tradeEntryDurationMs ?? '-'}</td>
      <td>{badge(row.mtfConfluenceStatus || 'NOT_STARTED')}</td><td>{row.mtfConfluenceDurationMs ?? '-'}</td>
      <td>{badge(row.takeProfitStatus || 'NOT_STARTED')}</td><td>{row.takeProfitDurationMs ?? '-'}</td>
      <td>{badge(row.dcaStatus || 'NOT_REQUIRED')}</td><td>{badge(row.breakEvenStatus || 'NOT_REQUIRED')}</td>
      <td>{badge(row.partialTakeProfitStatus || 'NOT_REQUIRED')}</td>
      <td>{badge(row.trailingStopLossStatus || 'NOT_REQUIRED')}</td>
      <td>{row.triggerSource || '-'}</td><td title={row.lifecycleComment || ''}>{row.lifecycleComment || '-'}</td>
      <td title={row.errorMessage || ''}>{row.errorMessage || '-'}</td></tr>)}</tbody></table>
    <FilteredEmpty rows={rows} visibleRows={visibleRows} empty="No trade lifecycle executions found in the last 24 hours." /></div>;
}

function BatchTable({ rows, onMissing, resetKey, page = 0 }) {
  const columns = [
    ['serialNumber', 'S.No', (_, index) => page * 50 + index + 1, 'numeric'],
    ['exchange', 'Exchange', row => row.exchange, 'select'], ['marketType', 'Market', row => row.marketType, 'select'],
    ['timeFrame', 'TF', row => `${row.timeFrame ?? ''}m`, 'select'], ['candleCloseTime', 'Candle Close', row => row.candleCloseTime],
    ['expected', 'Expected', row => row.expected, 'numeric'], ['received', 'Received', row => row.received, 'numeric'],
    ['missing', 'Missing', row => row.missing, 'numeric'], ['completion', 'Complete', row => `${Number(row.completion).toFixed(1)}%`, 'numeric'],
    ['batch', 'Batch', row => row.partial ? 'PARTIAL' : row.missing > 0 ? 'PENDING' : 'COMPLETE', 'select'],
    ['volumeRankStatus', 'Volume Rank', row => row.volumeRankStatus, 'select'],
    ['marketPulseStatus', 'Market Pulse', row => row.marketPulseStatus, 'select'],
  ];
  const { visibleRows, filterHeader, sortableHeader } = useTableControls(rows, columns, resetKey);
  return <div className="orchestration-table"><table><thead><tr>{sortableHeader}</tr>
    {filterHeader}</thead><tbody>{visibleRows.map(row => <tr key={row.batchCycleId}><td>{row.__rowNumber}</td><td>{row.exchange}</td><td>{row.marketType}</td><td>{row.timeFrame}m</td>
      <td>{row.candleCloseTime}</td><td>{row.expected}</td><td>{row.received}</td><td>{row.missing > 0 ? <button onClick={() => onMissing(row)}>{row.missing} View</button> : 0}</td><td>{Number(row.completion).toFixed(1)}%</td>
      <td>{row.partial ? 'PARTIAL' : row.missing > 0 ? 'PENDING' : 'COMPLETE'}</td><td>{badge(row.volumeRankStatus)}</td><td>{badge(row.marketPulseStatus)}</td></tr>)}</tbody></table>
    <FilteredEmpty rows={rows} visibleRows={visibleRows} empty="No batches found." /></div>;
}

function MissingSymbolsDialog({ data, onClose }) {
  return <div className="orchestration-dialog-backdrop" role="dialog" aria-modal="true"><div className="orchestration-dialog">
    <button className="close" onClick={onClose}>Close</button><h3>Missing symbols</h3>
    <p>{data.batch.exchange} / {data.batch.marketType} / {data.batch.timeFrame}m / {data.batch.candleCloseTime}</p>
    <p><strong>{data.total}</strong> qualified symbols have not completed signal generation.</p>
    {data.symbols.length ? <div className="orchestration-symbol-list">{data.symbols.map((symbol, index) =>
      <span key={symbol}>{symbol}{index < data.symbols.length - 1 ? ',' : ''}</span>)}</div>
      : <p>No missing symbols.</p>}
  </div></div>;
}

function SchedulerTable({ rows, resetKey }) {
  const columns = [['serialNumber', 'S.No', (_, index) => index + 1, 'numeric'],
    ['schedulerName', 'Scheduler', row => row.schedulerName], ['category', 'Category', row => row.category, 'select'],
    ['schedule', 'Schedule', row => row.schedule], ['enabled', 'Enabled', row => row.enabled ? 'ENABLED' : 'DISABLED', 'select']];
  const { visibleRows, filterHeader, sortableHeader } = useTableControls(rows, columns, resetKey);
  return <div className="orchestration-table"><table><thead><tr>{sortableHeader}</tr>{filterHeader}</thead>
    <tbody>{visibleRows.map(row => <tr key={row.schedulerName}><td>{row.__rowNumber}</td><td>{row.schedulerName}</td><td>{row.category}</td><td>{row.schedule}</td><td>{badge(row.enabled ? 'ENABLED' : 'DISABLED')}</td></tr>)}</tbody></table>
    <FilteredEmpty rows={rows} visibleRows={visibleRows} empty="No schedulers found." /></div>;
}

function SymbolUniverseTable({ rows, onSetActive, onSetTradingAllowed, resetKey }) {
  const symbols = Object.values(rows.reduce((grouped, row) => {
    const key = `${row.exchange}|${row.marketType}|${row.symbol}`;
    const item = grouped[key] || { exchange: row.exchange, marketType: row.marketType,
      symbol: row.symbol, timeFrames: [], ids: [], activeRows: 0, tradingRows: 0,
      sources: new Set(), approvals: new Set() };
    item.timeFrames.push(row.timeFrame);
    item.ids.push(row.id);
    if (row.active) item.activeRows += 1;
    if (row.tradingAllowed) item.tradingRows += 1;
    item.sources.add(row.source);
    item.approvals.add(row.approvalStatus);
    grouped[key] = item;
    return grouped;
  }, {})).sort((a, b) => a.exchange.localeCompare(b.exchange)
    || a.marketType.localeCompare(b.marketType) || a.symbol.localeCompare(b.symbol));
  symbols.forEach((item, index) => { item.serialNumber = index + 1; item.active = item.activeRows === item.timeFrames.length;
    item.partiallyActive = item.activeRows > 0 && !item.active;
    item.tradingAllowed = item.exchange === 'BITGET' && item.marketType === 'PERPS'
      && item.tradingRows === item.timeFrames.length; });
  const columns = [['serialNumber', 'S.No', item => item.serialNumber, 'numeric'], ['exchange', 'Exchange', item => item.exchange, 'select'],
    ['marketType', 'Market Type', item => item.marketType, 'select'], ['symbol', 'Symbol', item => item.symbol, 'select'],
    ['timeFrames', 'Timeframes', item => item.timeFrames.slice().sort((a, b) => a - b).join(', ')],
    ['status', 'Status', item => item.active ? 'ACTIVE' : item.partiallyActive ? 'PARTIAL' : 'INACTIVE', 'select'],
    ['approval', 'Approval', item => Array.from(item.approvals).join(', '), 'select'],
    ['trading', 'Trading', item => item.tradingAllowed ? 'ALLOWED' : 'DISABLED', 'select'],
    ['source', 'Source', item => Array.from(item.sources).join(', '), 'select'],
    ['action', 'Admin action', item => item.active ? 'Deactivate' : 'Activate', 'none']];
  const { visibleRows, filterHeader, sortableHeader } = useTableControls(symbols, columns, resetKey);
  return <div className="orchestration-table"><table><thead><tr>
    {sortableHeader}
  </tr>{filterHeader}</thead><tbody>{visibleRows.map(item => {
    const index = item.serialNumber - 1;
    const active = item.activeRows === item.timeFrames.length;
    const partiallyActive = item.activeRows > 0 && !active;
    return <tr key={`${item.exchange}-${item.marketType}-${item.symbol}`}><td>{index + 1}</td>
      <td>{item.exchange}</td><td>{item.marketType}</td><td>{item.symbol}</td>
      <td>{item.timeFrames.sort((a, b) => a - b).join(', ')}</td>
      <td>{badge(active ? 'ACTIVE' : partiallyActive ? 'PARTIAL' : 'INACTIVE')}</td>
      <td>{Array.from(item.approvals).join(', ')}</td>
      <td>{badge(item.tradingAllowed ? 'ALLOWED' : 'DISABLED')}</td>
      <td>{Array.from(item.sources).join(', ')}</td>
      <td><button onClick={() => onSetActive(item.exchange, item.marketType, item.symbol, !active)}>{active ? 'Deactivate' : 'Activate'}</button>
        <button disabled={!active || (item.exchange !== 'BITGET' || item.marketType !== 'PERPS')}
          title={item.exchange !== 'BITGET' || item.marketType !== 'PERPS' ? 'Signals only: execution is unsupported' : ''}
          onClick={() => onSetTradingAllowed(item.ids, !item.tradingAllowed)}>
          {item.tradingAllowed ? 'Disable trading' : 'Allow trading'}</button></td></tr>;
  })}</tbody></table><FilteredEmpty rows={symbols} visibleRows={visibleRows} empty="No symbols configured." /></div>;
}

function numericValue(value) {
  const match = String(value ?? '').replace(/,/g, '').match(/-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : Number.NaN;
}

function matchesNumericFilter(value, filter) {
  const expression = String(filter || '').trim();
  if (!expression) return true;
  const match = expression.match(/^(<=|>=|<|>|=)?\s*(-?\d+(?:\.\d+)?)$/);
  if (!match) return false;
  const actual = numericValue(value);
  if (!Number.isFinite(actual)) return false;
  const expected = Number(match[2]);
  switch (match[1] || '=') {
    case '<': return actual < expected;
    case '<=': return actual <= expected;
    case '>': return actual > expected;
    case '>=': return actual >= expected;
    default: return actual === expected;
  }
}

function useTableControls(rows, columns, resetKey) {
  const [columnFilters, setColumnFilters] = useState({});
  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  useEffect(() => {
    setColumnFilters({});
    setSort({ key: '', direction: 'asc' });
  }, [resetKey]);
  const numberedRows = rows.map((row, index) => ({
    ...row, __sourceIndex: index, __rowNumber: columns[0][2](row, index),
  }));
  const filteredRows = numberedRows.filter(row => columns.every(([key, , valueOf, filterType]) => {
    if (filterType === 'none') return true;
    const filter = String(columnFilters[key] || '').trim().toLowerCase();
    const value = String(key === 'serialNumber' ? row.__rowNumber : valueOf(row, row.__sourceIndex) ?? '').toLowerCase();
    if (!filter) return true;
    if (filterType === 'select') return value === filter;
    if (filterType === 'numeric') return matchesNumericFilter(value, filter);
    return value.includes(filter);
  }));
  const visibleRows = [...filteredRows].sort((left, right) => {
    if (!sort.key) return left.__sourceIndex - right.__sourceIndex;
    const column = columns.find(([key]) => key === sort.key);
    const leftValue = sort.key === 'serialNumber' ? left.__rowNumber : column[2](left, left.__sourceIndex);
    const rightValue = sort.key === 'serialNumber' ? right.__rowNumber : column[2](right, right.__sourceIndex);
    const numericColumn = column[3] === 'numeric';
    const leftNumber = numericValue(leftValue);
    const rightNumber = numericValue(rightValue);
    const comparison = numericColumn && Number.isFinite(leftNumber) && Number.isFinite(rightNumber)
      ? leftNumber - rightNumber
      : String(leftValue ?? '').localeCompare(String(rightValue ?? ''), undefined, { numeric: true });
    return sort.direction === 'asc' ? comparison : -comparison;
  });
  const optionsFor = valueOf => [...new Set(numberedRows.map(row => String(valueOf(row, row.__sourceIndex) ?? '')).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const sortableHeader = columns.map(([key, label, , filterType]) => <th key={key}>
    <button type="button" className="orchestration-sort" disabled={filterType === 'none'}
      aria-label={`Sort by ${label}`} onClick={() => setSort(current => ({
        key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
      }))}>{label}<span aria-hidden="true">{sort.key === key ? (sort.direction === 'asc' ? ' ▲' : ' ▼') : ' ↕'}</span></button>
  </th>);
  const filterHeader = <tr className="orchestration-column-filters">{columns.map(([key, label, valueOf, filterType]) =>
    <th key={key}>{filterType === 'none' ? null : filterType === 'select'
      ? <select aria-label={`Filter ${label}`} value={columnFilters[key] || ''}
          onChange={event => setColumnFilters(current => ({ ...current, [key]: event.target.value }))}>
          <option value="">All</option>{optionsFor(valueOf).map(value => <option key={value} value={value.toLowerCase()}>{value}</option>)}
        </select>
      : <input type="text" aria-label={`Filter ${label}`} placeholder={filterType === 'numeric' ? 'e.g. >= 10' : 'Filter'}
          value={columnFilters[key] || ''} onChange={event => setColumnFilters(current => ({ ...current, [key]: event.target.value }))} />}</th>)}</tr>;
  return { visibleRows, filterHeader, sortableHeader };
}

function FilteredEmpty({ rows, visibleRows, empty }) {
  if (visibleRows.length) return null;
  return <p className="orchestration-empty">{rows.length ? 'No records match the column filters.' : empty}</p>;
}

function Pagination({ page, pages, setPage }) {
  return <div className="orchestration-pagination"><button disabled={page <= 0} onClick={() => setPage(page - 1)}>Previous</button>
    <span>Page {page + 1} of {pages}</span><button disabled={page + 1 >= pages} onClick={() => setPage(page + 1)}>Next</button></div>;
}

function CycleDetails({ data, onClose }) {
  return <div className="orchestration-dialog-backdrop" role="dialog" aria-modal="true"><div className="orchestration-dialog">
    <button className="close" onClick={onClose}>Close</button><h3>Cycle {data.cycle.cycleId}</h3>
    <dl><dt>Scope</dt><dd>{data.cycle.exchange} / {data.cycle.marketType} / {data.cycle.symbol}</dd>
      <dt>Candle</dt><dd>{data.cycle.timeFrame}m — {data.cycle.candleCloseTime}</dd><dt>Status</dt><dd>{badge(data.cycle.status)}</dd></dl>
    <h4>Stage timeline</h4>{data.timeline?.length ? <ol className="orchestration-timeline">{data.timeline.map((attempt, index) =>
      <li key={`${attempt.stage}-${index}`}><strong>{attempt.stage}</strong> {badge(attempt.status)}<span>{formatDate(attempt.startedAt)} · {attempt.durationMs || 0} ms</span></li>)}</ol>
      : <p>No attempts recorded yet.</p>}
  </div></div>;
}
